/**
 * search.js — PredictiveSearch custom element
 * Live search via /search/suggest with:
 *  • AbortController (cancel stale fetches)
 *  • Debounced input (180ms)
 *  • Loading + empty states (data-state on content)
 *  • localStorage recent searches (max 6)
 *  • Clear button, keyboard nav (ArrowUp/Down), Enter submits
 */

const RECENTS_KEY = 'bh:recent-searches';
const RECENTS_LIMIT = 6;
const DEBOUNCE_MS = 180;
const MIN_QUERY_LEN = 2;

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((t) => typeof t === 'string' && t.trim()) : [];
  } catch {
    return [];
  }
}

function writeRecents(list) {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_LIMIT)));
  } catch {}
}

function pushRecent(term) {
  const clean = (term || '').trim();
  if (clean.length < MIN_QUERY_LEN) return;
  const lower = clean.toLowerCase();
  const existing = readRecents().filter((t) => t.toLowerCase() !== lower);
  writeRecents([clean, ...existing]);
}

function removeRecent(term) {
  writeRecents(readRecents().filter((t) => t !== term));
}

function clearRecents() {
  try { localStorage.removeItem(RECENTS_KEY); } catch {}
}

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.field        = this.querySelector('[data-search-field]');
    this.inputWrap    = this.querySelector('[data-search-input-wrap]');
    this.clearBtn     = this.querySelector('[data-search-clear]');
    this.content      = this.querySelector('[data-search-content]');
    this.results      = this.querySelector('[data-search-results]');
    this.overlay      = this.closest('[data-search-overlay]');
    this.recentBlock  = this.querySelector('[data-search-recent]');
    this.recentList   = this.querySelector('[data-search-recent-list]');
    this.recentClear  = this.querySelector('[data-search-recent-clear]');

    if (!this.field || !this.results || !this.content) return;

    this.cache = new Map();
    this.controller = null;
    this.debounced = this._debounce(this._runQuery.bind(this), DEBOUNCE_MS);

    this.field.addEventListener('input', this._onInput.bind(this));
    this.field.addEventListener('keydown', this._onFieldKeydown.bind(this));

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', this._onClear.bind(this));
    }
    if (this.recentClear) {
      this.recentClear.addEventListener('click', () => {
        clearRecents();
        this._renderRecents();
      });
    }

    // Recent item click / remove (delegated)
    if (this.recentList) {
      this.recentList.addEventListener('click', this._onRecentClick.bind(this));
    }

    // Trending tag click → populate input instead of navigating
    this.querySelectorAll('[data-trending-term]').forEach((tag) => {
      tag.addEventListener('click', (e) => {
        e.preventDefault();
        const term = tag.getAttribute('data-trending-term') || '';
        this._setQuery(term);
        this.field.focus();
      });
    });

    this.addEventListener('keydown', this._onContainerKeydown.bind(this));

    // Save the current query to recents when the user clicks a result link
    this.addEventListener('click', (e) => {
      if (!e.target.closest('[data-search-nav]')) return;
      const q = this.field.value.trim();
      if (q.length >= MIN_QUERY_LEN) pushRecent(q);
    });

    this._renderRecents();
    this._setState('idle');
  }

  // ── Public: called when overlay opens
  reset() {
    this.field.value = '';
    this.inputWrap?.classList.remove('has-query');
    this.results.innerHTML = '';
    this._setState('idle');
    this._setLoading(false);
    this._renderRecents();
  }

  // ── State
  _setState(state) {
    this.content.setAttribute('data-state', state);
    this.field.setAttribute('aria-expanded', state === 'results' ? 'true' : 'false');
  }

  _setLoading(on) {
    if (!this.overlay) return;
    this.overlay.classList.toggle('is-loading', !!on);
  }

  // ── Input handlers
  _onInput() {
    const query = this.field.value;
    this.inputWrap?.classList.toggle('has-query', query.trim().length > 0);

    if (query.trim().length < MIN_QUERY_LEN) {
      this._cancelInflight();
      this._setLoading(false);
      this._setState('idle');
      return;
    }

    this._setLoading(true);
    this.debounced(query.trim());
  }

  _onClear() {
    this.field.value = '';
    this.inputWrap?.classList.remove('has-query');
    this._cancelInflight();
    this._setLoading(false);
    this.results.innerHTML = '';
    this._setState('idle');
    this.field.focus();
  }

  _setQuery(term) {
    this.field.value = term;
    this.inputWrap?.classList.toggle('has-query', term.trim().length > 0);
    this.field.dispatchEvent(new Event('input', { bubbles: true }));
  }

  _onFieldKeydown(e) {
    if (e.key === 'Enter') {
      const q = this.field.value.trim();
      if (q.length >= MIN_QUERY_LEN) {
        pushRecent(q);
      }
      // Let the native form behavior run via constructed URL
      e.preventDefault();
      const url = `${window.theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(q)}&type=product`;
      window.location.href = url;
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const first = this._navItems()[0];
      if (first) first.focus();
    }
  }

  _onContainerKeydown(e) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = this._navItems();
    if (!items.length) return;
    const idx = items.indexOf(document.activeElement);
    if (idx === -1) return; // handled at field level

    e.preventDefault();
    if (e.key === 'ArrowDown') {
      (items[idx + 1] || items[0]).focus();
    } else {
      if (idx === 0) {
        this.field.focus();
      } else {
        items[idx - 1].focus();
      }
    }
  }

  _navItems() {
    return Array.from(this.querySelectorAll('[data-search-nav]'));
  }

  // ── Recents
  _renderRecents() {
    if (!this.recentBlock || !this.recentList) return;
    const recents = readRecents();
    if (recents.length === 0) {
      this.recentBlock.hidden = true;
      this.recentList.innerHTML = '';
      return;
    }
    this.recentBlock.hidden = false;
    this.recentList.innerHTML = recents.map((term) => `
      <li class="search-recent__item">
        <div class="search-row" data-recent-term="${escapeHtml(term)}">
          <button type="button" class="flex-1 text-left" data-recent-fill>
            <span>${escapeHtml(term)}</span>
          </button>
          <button type="button" class="search-row__remove" data-recent-remove aria-label="Remove">×</button>
        </div>
      </li>
    `).join('');
  }

  _onRecentClick(e) {
    const row = e.target.closest('[data-recent-term]');
    if (!row) return;
    const term = row.getAttribute('data-recent-term') || '';

    if (e.target.closest('[data-recent-remove]')) {
      removeRecent(term);
      this._renderRecents();
      return;
    }
    if (e.target.closest('[data-recent-fill]')) {
      this._setQuery(term);
      this.field.focus();
    }
  }

  // ── Fetch
  _cancelInflight() {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  async _runQuery(query) {
    if (this.cache.has(query)) {
      this._paint(this.cache.get(query));
      this._setLoading(false);
      return;
    }

    this._cancelInflight();
    this.controller = new AbortController();

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('section_id', 'predictive-search');
    params.set('resources[type]', 'product,collection,article,page,query');
    params.set('resources[limit]', '6');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'hide');
    params.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku,tag,body,author');

    const routes      = (window.theme && window.theme.routes) || {};
    const suggestPath = routes.predictive_search_url
      || ((routes.root || '/').replace(/\/?$/, '/') + 'search/suggest');
    const url         = `${suggestPath}?${params.toString()}`;

    try {
      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
        signal: this.controller.signal
      });
      if (!res.ok) return;
      const html = await res.text();
      if (!html || !html.trim()) {
        this.results.innerHTML = '';
        this._setState('idle');
        return;
      }
      this.cache.set(query, html);
      this._paint(html);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[search] fetch failed:', err);
    } finally {
      this._setLoading(false);
      this.controller = null;
    }
  }

  _paint(html) {
    this.results.innerHTML = html;

    // Re-trigger stagger animations by forcing a reflow
    this.results.querySelectorAll('.search-result-item').forEach((el) => {
      el.style.animation = 'none';
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;
      el.style.animation = '';
    });

    // Server response always fills the results container — both populated
    // and empty states are rendered by predictive-search.liquid itself.
    this._setState('results');
  }

  // ── Utility
  _debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);

// Expose hook so the overlay open/close code can reset input state.
window.bhPredictiveSearch = {
  reset() {
    document.querySelector('predictive-search')?.reset?.();
  },
  pushRecent,
};
